import React, { useState } from 'react';
import styles from './PlaceListWidget.module.scss';

type PlaceItem = {
  place_id: string;
  name: string;
  types: string[];
  photos: string[];
  rating?: number;
  rating_count?: number;
  opening_hours?: string;
  address?: string;
  ctas?: ('view_details' | 'add_to_itinerary')[];
};

type PlaceListPayload = {
  title: string;
  items: PlaceItem[];
};

const GOOGLE_MAPS_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();

function normalizeGooglePhotoUrl(url?: string): string | undefined {
  if (!url) return undefined;
  const raw = url.startsWith('//') ? `https:${url}` : url;
  if (!raw.startsWith('http')) return undefined;

  // Places API media URLs often arrive without key/max size; make them browser-friendly.
  if (raw.includes('places.googleapis.com/v1/') && GOOGLE_MAPS_KEY && !raw.includes('key=')) {
    const separator = raw.includes('?') ? '&' : '?';
    return `${raw}${separator}key=${encodeURIComponent(GOOGLE_MAPS_KEY)}&maxHeightPx=360&maxWidthPx=640`;
  }
  return raw;
}

function fallbackStaticMap(address?: string, name?: string): string | undefined {
  if (!GOOGLE_MAPS_KEY) return undefined;
  // Prefer the place name for the map query; fall back to address only if name is empty.
  const query = encodeURIComponent(name || address || 'city center');
  return `https://maps.googleapis.com/maps/api/staticmap?center=${query}&zoom=12&size=640x360&scale=2&markers=color:red%7C${query}&key=${encodeURIComponent(GOOGLE_MAPS_KEY)}`;
}

function fallbackEmbedMap(address?: string, name?: string): string | undefined {
  // Prefer the place name so the embed map shows the correct destination.
  const query = (name || address || '').trim();
  if (!query) return undefined;
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
}

function placeKind(types: string[]): 'airport' | 'city' | 'landmark' {
  if (types.some((type) => ['airport', 'airport_terminal'].includes(type))) return 'airport';
  if (types.some((type) => ['locality', 'political', 'city'].includes(type))) return 'city';
  return 'landmark';
}

export function PlaceListWidget({
  payload,
  onAddPlace,
}: {
  payload: PlaceListPayload;
  onAddPlace: (value: { label: string; id: string; kind: 'airport' | 'city' | 'landmark' }) => void;
}) {
  const singleItem = payload.items.length === 1;
  const [failedMedia, setFailedMedia] = useState<Record<string, boolean>>({});

  return (
    <section className={styles.root} aria-label={payload.title || 'Places'}>
      {payload.title ? <h3 className={styles.title}>{payload.title}</h3> : null}
      <div className={`${styles.grid} ${singleItem ? styles.single : ''}`}>
        {payload.items.map((item) => {
          const kind = placeKind(item.types || []);
          const typeLabels = (item.types || []).map((type) => type.replace(/_/g, ' ')).slice(0, singleItem ? 6 : 3);
          const previewSrc = normalizeGooglePhotoUrl(item.photos?.[0]) || fallbackStaticMap(item.address, item.name);
          const embedSrc = fallbackEmbedMap(item.address, item.name);
          const showImage = Boolean(previewSrc && !failedMedia[item.place_id]);
          return (
            <article key={item.place_id} className={styles.card}>
              <div className={styles.mediaWrap}>
                {showImage ? (
                  <img
                    src={previewSrc}
                    alt={item.name}
                    className={styles.media}
                    onError={() => setFailedMedia((current) => ({ ...current, [item.place_id]: true }))}
                  />
                ) : embedSrc ? (
                  <iframe
                    title={`Map of ${item.name}`}
                    src={embedSrc}
                    className={styles.mapFrame}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : <div className={styles.mediaPlaceholder} aria-hidden="true" />}
              </div>

              <div className={styles.content}>
                <h4 className={styles.name}>{item.name}</h4>
                {item.address ? <p className={styles.address}>{item.address}</p> : null}

                <div className={styles.tags}>
                  {typeLabels.map((type) => <span key={type} className={styles.tag}>{type}</span>)}
                </div>

                {item.ctas?.includes('add_to_itinerary') ? (
                  <button
                    type="button"
                    className={styles.addButton}
                    onClick={() => onAddPlace({ label: item.name, id: item.place_id, kind })}
                  >
                    Add to itinerary
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

